import { Divider, Stack, Typography } from "@mui/material";
import type { PropsWithChildren } from "react";
import { useMemo } from "react";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import CompleteDialogIcon from "@entrilia/shared/icons/CompleteDialogIcon";
import type { ClientInfo } from "../../../../../../api/types/clientTypes";
import useUserContext from "../../../../../../context/useUserContext";
import { ValidatedFieldsContainer } from "../../../../reporting/report-lists/reports/copyto/validationDetails/ValidationDetailsContainer";
import type { SelectedCompanyTile } from "../../../hooks/validationStateSlice";
import { isSmartViewWidget } from "../../../models/isTileObjectSmartView";
import NoWidgetSelected from "./NoWidgetSelected";

interface Props {
  companyTile: SelectedCompanyTile | undefined;
}
const ValidationDetailsContainer = ({ companyTile }: Props) => {
  const { clients } = useUserContext();

  const isSmartView = useMemo(() => {
    return companyTile && isSmartViewWidget(companyTile.tile.widget);
  }, [companyTile]);

  const companyInfo = useMemo(() => {
    return clients.find((c) => c.clientCode === companyTile?.companyCode);
  }, [clients, companyTile]);

  if (!companyTile) {
    return <NoWidgetSelected />;
  }

  if (isTileWaitingOrValidating(companyTile)) {
    return <InlineLoader />;
  }

  if (companyTile?.error) {
    return (
      <Stack sx={{ alignItems: "center", justifyContent: "center", width: "100%" }}>
        <Typography color="error">An error occurred while validating the report</Typography>
      </Stack>
    );
  }

  if (companyTile.result?.hasRequiredDataset === false) {
    return (
      <Stack sx={{ textAlign: "center", width: "100%", justifyContent: "center" }}>
        <Typography color="error" sx={{ px: 2 }}>
          {`Dataset ${companyTile.result?.requiredDataSetName ?? ""} is missing in the target company.`}
        </Typography>
      </Stack>
    );
  }

  return (
    <ValidationDetailsHeader companyTile={companyTile} companyInfo={companyInfo}>
      {isSmartView ? (
        <SmartViewValidationDetailsContainer companyTile={companyTile} />
      ) : (
        <ArbitraryValidationDetailsContainer />
      )}
    </ValidationDetailsHeader>
  );
};

const ValidationDetailsHeader = ({
  companyTile,
  companyInfo,
  children,
}: PropsWithChildren<{
  companyTile: SelectedCompanyTile;
  companyInfo: ClientInfo | undefined;
}>) => {
  return (
    <Stack sx={{ width: "100%" }}>
      <Stack spacing={0.5} useFlexGap sx={{ py: 2, px: 2.5 }}>
        <Typography variant="subtitle1">{companyTile.tile.widget.nameUserInput}</Typography>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
          <Typography color="secondary">{companyInfo?.title}</Typography>
          <Typography color="secondary">&bull;</Typography>
          <Typography color="secondary">{companyTile.dashboard.nameUserInput}</Typography>
        </Stack>
      </Stack>
      <Divider />
      {children}
    </Stack>
  );
};

const SmartViewValidationDetailsContainer = ({ companyTile }: { companyTile: SelectedCompanyTile }) => {
  return (
    <>
      <Stack spacing={2} useFlexGap sx={{ py: 2, px: 2.5 }}>
        <Typography color="secondary">Fields that will be copied with the report</Typography>
      </Stack>
      <Stack sx={{ flex: 1, width: "100%", px: 2.5 }}>
        <ScrollableFlexContainer>
          {companyTile.result && <ValidatedFieldsContainer validationResult={companyTile.result} />}
        </ScrollableFlexContainer>
      </Stack>
    </>
  );
};

const ArbitraryValidationDetailsContainer = () => {
  return (
    <Stack spacing={2} useFlexGap sx={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
      <CompleteDialogIcon sx={{ height: 80, width: 80 }} />
      <Typography color="secondary">All settings will be copied.</Typography>
    </Stack>
  );
};

export default ValidationDetailsContainer;

function isTileWaitingOrValidating(tile: SelectedCompanyTile | undefined) {
  return tile?.validating || (!tile?.validated && !tile?.validating && !tile?.error);
}
